/**
 * Adaptador para procesar incumplimientos
 * Filtra y organiza incumplimientos pedagógicos y económicos
 */

// Constantes para evaluación de incumplimientos
const nonComplianceTypes = {
  economic: 'económico',
  pedagogic: 'pedagógico'
};

const evaluationConstants = {
  maxDaysForEvaluation: 365,
  cupsSeparator: ', ',
  nonComplianceCategories: {
    both: 'both',
    pedagogic: 'pedagogic',
    economic: 'economic',
    none: 'none'
  }
};

export class NonComplianceAdapter {
  /**
   * Filtra incumplimientos pedagógicos dentro del último año
   * @param {array} nonCompliances - Arreglo de incumplimientos
   * @param {function} getDaysBetweenDates - Función para calcular días entre fechas
   * @returns {array}
   */
  getPedagogicNonCompliances(nonCompliances, getDaysBetweenDates) {
    if (!nonCompliances || nonCompliances.length === 0) {
      return [];
    }

    return nonCompliances.filter((nonCompliance) => {
      const isPedagogic = nonCompliance.TipoIncumplimiento.toLowerCase() !== nonComplianceTypes.economic;
      const isWithinYear = getDaysBetweenDates(new Date(), nonCompliance.FechaCita) < evaluationConstants.maxDaysForEvaluation;
      return isPedagogic && isWithinYear;
    });
  }

  /**
   * Filtra incumplimientos económicos dentro del último año
   * @param {array} nonCompliances - Arreglo de incumplimientos
   * @param {function} getDaysBetweenDates - Función para calcular días entre fechas
   * @returns {array}
   */
  getEconomicNonCompliances(nonCompliances, getDaysBetweenDates) {
    if (!nonCompliances || nonCompliances.length === 0) {
      return [];
    }

    return nonCompliances.filter((nonCompliance) => {
      const isEconomic = nonCompliance.TipoIncumplimiento.toLowerCase() !== nonComplianceTypes.pedagogic;
      const isWithinYear = getDaysBetweenDates(new Date(), nonCompliance.FechaCita) < evaluationConstants.maxDaysForEvaluation;
      return isEconomic && isWithinYear;
    });
  }

  /**
   * Obtiene las descripciones CUPS de un arreglo de incumplimientos
   * @param {array} nonCompliances - Arreglo de incumplimientos
   * @returns {string} - String con descripciones separadas por coma
   */
  getCupsDescriptions(nonCompliances) {
    if (!nonCompliances || nonCompliances.length === 0) {
      return '';
    }

    return nonCompliances
      .map((elem) => elem.DesCups)
      .join(evaluationConstants.cupsSeparator);
  }

  /**
   * Determina el tipo de incumplimiento predominante
   * @param {array} pedagogicNonCompliances
   * @param {array} economicNonCompliances
   * @returns {string} - 'both' | 'pedagogic' | 'economic' | 'none'
   */
  determineNonComplianceType(pedagogicNonCompliances, economicNonCompliances) {
    const hasPedagogic = pedagogicNonCompliances && pedagogicNonCompliances.length > 0;
    const hasEconomic = economicNonCompliances && economicNonCompliances.length > 0;

    if (hasPedagogic && hasEconomic) {
      return evaluationConstants.nonComplianceCategories.both;
    }
    if (hasPedagogic) {
      return evaluationConstants.nonComplianceCategories.pedagogic;
    }
    if (hasEconomic) {
      return evaluationConstants.nonComplianceCategories.economic;
    }
    return evaluationConstants.nonComplianceCategories.none;
  }

  /**
   * Valida si hay incumplimientos
   * @param {array} nonCompliances
   * @returns {boolean}
   */
  hasNonCompliances(nonCompliances) {
    return nonCompliances && nonCompliances.length > 0;
  }
}

export const nonComplianceAdapter = new NonComplianceAdapter();
